import React, { useState, useEffect } from 'react';
import { FiSearch, FiEdit2, FiTrash2, FiShield, FiPlus, FiX } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const DashboardUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');

  // Edit User Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({ nama: '', email: '', username: '', role: '', organisasi: '', password: '' });

  // Add User Modal
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ nama: '', email: '', username: '', password: '', role: 'viewer', organisasi: '' });
  const [formLoading, setFormLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      const res = await api.get('/admin/users', { params });
      setUsers(res.data.data);
    } catch (e) { toast.error('Gagal memuat users'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterRole]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await api.post('/admin/users', form);
      toast.success('User berhasil ditambahkan');
      setShowModal(false);
      setForm({ nama: '', email: '', username: '', password: '', role: 'viewer', organisasi: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Gagal menambahkan user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUserId(user._id);
    setEditForm({
      nama: user.nama || '',
      email: user.email || '',
      username: user.username || '',
      role: user.role || 'viewer',
      organisasi: user.organisasi || '',
      password: ''
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await api.put(`/admin/users/${editingUserId}`, editForm);
      toast.success('Data user berhasil diperbarui');
      setShowEditModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Gagal memperbarui user');
    } finally {
      setFormLoading(false);
    }
  };

  const generatePassword = (isEdit = false) => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890!@#$%^&*()_+";
    let pass = "";
    for (let i = 0; i < 12; i++) pass += chars[Math.floor(Math.random() * chars.length)];
    
    if (isEdit) {
      setEditForm({ ...editForm, password: pass });
    } else {
      setForm({ ...form, password: pass });
    }
    toast.success('Password dihasilkan: ' + pass, { duration: 5000 });
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}`, { role: newRole });
      toast.success('Role berhasil diubah');
      load();
    } catch (e) { toast.error('Gagal mengubah role'); }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await api.put(`/admin/users/${userId}`, { isActive: !isActive });
      toast.success(isActive ? 'User dinonaktifkan' : 'User diaktifkan');
      load();
    } catch (e) { toast.error('Gagal mengubah status'); }
  };

  const handleDelete = async (id, nama) => {
    if (!window.confirm(`Hapus user "${nama}"? Aksi ini tidak dapat dibatalkan.`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success('User dihapus'); load(); } catch (e) { toast.error('Gagal menghapus'); }
  };

  const handleSearch = (e) => { e.preventDefault(); load(); };

  return (
    <div>
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2>Kelola User</h2>
          <p>{users.length} user terdaftar</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <FiPlus /> Tambah User
        </button>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', flex: 1, minWidth: 250 }}>
          <input className="form-input" placeholder="Cari nama atau email..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ borderRadius: '6px 0 0 6px' }} />
          <button type="submit" className="btn btn-primary" style={{ borderRadius: '0 6px 6px 0' }}><FiSearch /></button>
        </form>
        <select className="form-select" style={{ width: 160 }} value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
          <option value="">Semua Role</option>
          <option value="admin">Admin</option>
          <option value="opd">OPD</option>
          <option value="viewer">Viewer</option>
        </select>
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>User</th><th>Username</th><th>Email</th><th>Organisasi</th><th>Role</th><th>Status</th><th>Aksi</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u._id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div className="user-avatar" style={{ width: 32, height: 32, fontSize: '0.7rem' }}>{u.nama?.charAt(0)}</div>
                      <strong style={{ fontSize: '0.85rem' }}>{u.nama}</strong>
                    </div>
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>{u.username}</td>
                  <td style={{ fontSize: '0.8rem' }}>{u.email}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{u.organisasi || '-'}</td>
                  <td>
                    <select className="form-select" style={{ width: 100, padding: '0.3rem 0.5rem', fontSize: '0.8rem' }} value={u.role} onChange={(e) => handleRoleChange(u._id, e.target.value)}>
                      <option value="admin">Admin</option>
                      <option value="opd">OPD</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </td>
                  <td>
                    <button className={`badge ${u.isActive ? 'badge-success' : 'badge-danger'}`} onClick={() => handleToggleActive(u._id, u.isActive)} style={{ cursor: 'pointer', border: 'none' }}>
                      {u.isActive ? 'Aktif' : 'Nonaktif'}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleEditClick(u)} title="Edit Profil"><FiEdit2 size={14} /></button>
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDelete(u._id, u.nama)} title="Hapus" style={{ color: 'var(--danger)' }}><FiTrash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL TAMBAH USER */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '1.1rem' }}>Tambah User Baru</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nama Lengkap *</label>
                  <input className="form-input" required value={form.nama} onChange={(e) => setForm({...form, nama: e.target.value})} placeholder="Misal: Budi Santoso" />
                </div>
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Username *</label>
                    <input className="form-input" required value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} placeholder="budi123" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input className="form-input" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="email@ngada.go.id" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input className="form-input" type="text" required value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} minLength="6" style={{ flex: 1 }} />
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => generatePassword(false)}>Generate</button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Role Akses *</label>
                  <select className="form-select" required value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}>
                    <option value="viewer">Viewer (Hanya Lihat)</option>
                    <option value="opd">OPD (Operator Data)</option>
                    <option value="admin">Admin (Akses Penuh)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Organisasi Pokok</label>
                  <input className="form-input" value={form.organisasi} onChange={(e) => setForm({...form, organisasi: e.target.value})} placeholder="Pemerintah / Dinas terkait" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Menyimpan...' : 'Simpan User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL EDIT USER */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '1.1rem' }}>Edit Profil User</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowEditModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nama Lengkap</label>
                  <input className="form-input" value={editForm.nama} onChange={(e) => setEditForm({...editForm, nama: e.target.value})} />
                </div>
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Username</label>
                    <input className="form-input" value={editForm.username} onChange={(e) => setEditForm({...editForm, username: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-input" type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Password Baru (Kosongkan jika tidak diubah)</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input className="form-input" type="text" value={editForm.password} onChange={(e) => setEditForm({...editForm, password: e.target.value})} placeholder="Masukkan password baru..." style={{ flex: 1 }} />
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => generatePassword(true)}>Generate</button>
                  </div>
                </div>
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Role Akses</label>
                    <select className="form-select" value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})}>
                      <option value="viewer">Viewer</option>
                      <option value="opd">OPD</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Organisasi</label>
                    <input className="form-input" value={editForm.organisasi} onChange={(e) => setEditForm({...editForm, organisasi: e.target.value})} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowEditModal(false)}>Batal</button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Memperbarui...' : 'Simpan Perubahan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardUsers;
